/**
 * Admin login: email + password, then optional TOTP step.
 * Design System: surface tokens, rounded-card, InputField, Button, full RTL + i18n.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@contexts/AuthContext';
import { staffLogin, staffVerifyTotp } from '@services/api';
import type { ApiError } from '@services/api';
import { SaaeLogo } from '@components/SaaeLogo';
import { Button } from '@components/Button/Button';
import { InputField } from '@components/Input';
import { useLanguage } from '@hooks/useLanguage';
import { BACKOFFICE_BASE } from '@lib/backofficeRoutes';
import { staffLoginSchema, staffTotpSchema, type StaffLoginValues, type StaffTotpValues } from '@schemas/formSchemas';

type Step = 'credentials' | 'totp';

export function AdminLoginPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const [step, setStep] = useState<Step>('credentials');
  const [pendingToken, setPendingToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isRtl = language === 'ar';

  const credForm = useForm<StaffLoginValues>({
    resolver: zodResolver(staffLoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const totpForm = useForm<StaffTotpValues>({
    resolver: zodResolver(staffTotpSchema),
    defaultValues: { code: '' },
  });

  async function handleCredentials(values: StaffLoginValues) {
    setError(null);
    setLoading(true);
    try {
      const result = await staffLogin(values.email, values.password);
      if (result.requiresTotp && result.pendingToken) {
        setPendingToken(result.pendingToken);
        setStep('totp');
      } else {
        await refreshAuth();
        navigate(BACKOFFICE_BASE, { replace: true });
      }
    } catch (err) {
      const ax = err as ApiError;
      setError(ax.message ?? t('admin.login.errorGeneric'));
    } finally {
      setLoading(false);
    }
  }

  async function handleTotp(values: StaffTotpValues) {
    setError(null);
    setLoading(true);
    try {
      await staffVerifyTotp(pendingToken, values.code);
      await refreshAuth();
      navigate(BACKOFFICE_BASE, { replace: true });
    } catch (err) {
      const ax = err as ApiError;
      setError(ax.message ?? t('admin.login.errorGeneric'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-surface-web px-4 py-10 font-sans"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div className="mb-8 flex justify-center">
        <SaaeLogo size="small" mode="light" className="!h-9" />
      </div>
      <div className="w-full max-w-md rounded-card border border-surface-border bg-surface-cards p-6 shadow-sm sm:p-8">
        <h1 className="mb-1 text-center text-xl font-bold text-primary font-sans sm:text-2xl">
          {t('admin.login.title')}
        </h1>
        <p className="mb-6 text-center text-sm text-primary/60 font-sans">
          {step === 'credentials' ? t('admin.login.subtitle') : t('admin.login.totpSubtitle')}
        </p>

        {step === 'credentials' && (
          <form onSubmit={credForm.handleSubmit(handleCredentials)} className="flex flex-col gap-4" noValidate>
            <InputField
              label={t('admin.login.emailLabel')}
              type="email"
              autoComplete="email"
              size="lg"
              fullWidth
              contentDir="ltr"
              state={credForm.formState.errors.email ? 'error' : 'default'}
              {...credForm.register('email')}
            />
            {credForm.formState.errors.email && (
              <p className="text-xs text-surface-error-primary">{credForm.formState.errors.email.message}</p>
            )}
            <InputField
              label={t('admin.login.passwordLabel')}
              type="password"
              autoComplete="current-password"
              size="lg"
              fullWidth
              state={credForm.formState.errors.password ? 'error' : 'default'}
              {...credForm.register('password')}
            />
            {credForm.formState.errors.password && (
              <p className="text-xs text-surface-error-primary">{credForm.formState.errors.password.message}</p>
            )}
            {error && (
              <p className="rounded-[16px] border border-surface-error-primary bg-surface-error-lighter/50 px-4 py-3 text-sm text-surface-error-primary font-sans" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={loading} loading={loading} data-testid="login-submit-btn">
              {t('admin.login.submit')}
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-brand-primary hover:underline">
                {t('admin.login.forgotPassword')}
              </Link>
            </div>
          </form>
        )}

        {step === 'totp' && (
          <form onSubmit={totpForm.handleSubmit(handleTotp)} className="flex flex-col gap-4" noValidate>
            <InputField
              label={t('admin.login.totpLabel')}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              size="lg"
              fullWidth
              contentDir="ltr"
              maxLength={6}
              placeholder="000000"
              state={totpForm.formState.errors.code ? 'error' : 'default'}
              {...totpForm.register('code')}
            />
            {totpForm.formState.errors.code && (
              <p className="text-xs text-surface-error-primary">{totpForm.formState.errors.code.message}</p>
            )}
            {error && (
              <p className="rounded-[16px] border border-surface-error-primary bg-surface-error-lighter/50 px-4 py-3 text-sm text-surface-error-primary font-sans" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={loading} loading={loading} data-testid="totp-submit-btn">
              {t('admin.login.submit')}
            </Button>
            <button
              type="button"
              className="text-center text-sm text-primary/60 hover:underline"
              onClick={() => { setStep('credentials'); setError(null); totpForm.reset(); }}
            >
              {t('admin.login.backToLogin')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
